import { format } from 'date-fns';
import { ChangeEvent, useEffect, useState, useCallback } from 'react';

import AlertDetailsModal from './AlertDetailsModal';
import ResolveModal from './ResolveModal';
import SmsConversation from './SmsConversation';
import { PresenceStatus, BackfillStatus } from './StatusBadge';

import { noshowAlertsAPI } from '@/services/noshowAlertsAPI';
import {
  NoshowAlert,
  NoshowAlertFilters,
  NoshowAlertStats,
  NoshowPagination,
  ResolutionType,
} from '@/types/noshowAlerts';

import Alert from '@/components/Alert';
import Card from '@/components/Card';
import FormElement from '@/components/FormElement';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import Paginator from '@/components/Paginator/Paginator';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import TextSelect from '@/components/TextSelect';
import TextStack from '@/components/TextStack';
import { Body, Caption } from '@/components/Typography';
import Button from '@/components/Button';
import Link from '@/elements/Link';
import { TableCell, TableRow } from '@/elements/Table';
import FormColumns from '@/form/FormColumns';

const RESOLVED_STATUSES = [
  'resolved',
  'confirmed_present',
  'backfill_declined',
];

const headerFields = [
  'Job',
  'Worker',
  'Supervisor',
  'Customer',
  'No-Show?',
  'Backfill?',
  'SMS Sent',
  'Created',
  'Actions',
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'needs_action', label: 'Needs Action' },
  { value: 'active', label: 'In Progress' },
  { value: 'escalated', label: 'No Response' },
  { value: 'backfill_requested', label: 'Backfill Requested' },
  { value: 'backfill_declined', label: 'Backfill Declined' },
  { value: 'multi_worker_flagged', label: 'Multi-Worker' },
  { value: 'confirmed_noshow', label: 'Confirmed No-Show' },
  { value: 'confirmed_present', label: 'Confirmed Present' },
];

const NoshowAlerts = () => {
  const [alerts, setAlerts] = useState<NoshowAlert[]>([]);
  const [pagination, setPagination] = useState<NoshowPagination | null>(null);
  const [stats, setStats] = useState<NoshowAlertStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [showResolved, setShowResolved] = useState(false);

  const [filters, setFilters] = useState<NoshowAlertFilters>({
    exclude_resolved: true,
    page: 1,
    per_page: 25,
  });

  const [resolveModal, setResolveModal] = useState<NoshowAlert | null>(null);
  const [detailsAlertId, setDetailsAlertId] = useState<number | null>(null);
  const [smsAlertId, setSmsAlertId] = useState<number | null>(null);

  const loadAlerts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await noshowAlertsAPI.getAlerts({
        ...filters,
        page: currentPage,
        per_page: itemsPerPage,
      });

      if (response.success) {
        setAlerts(response.data || []);
        setPagination(response.pagination || null);
      }
    } catch (err: any) {
      console.error('[NoshowAlerts] Error:', err);
      setError(err.response?.data?.errors?.[0] || 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, itemsPerPage]);

  const loadStats = useCallback(async () => {
    try {
      const response = await noshowAlertsAPI.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err: any) {
      console.error('[NoshowAlerts] Stats error:', err);
    }
  }, []);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleStatusChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setCurrentPage(1);
    setFilters({
      ...filters,
      status: value || undefined,
      page: 1,
    });
  };

  const handleShowResolvedToggle = () => {
    const newShowResolved = !showResolved;
    setShowResolved(newShowResolved);
    setCurrentPage(1);
    setFilters({
      ...filters,
      exclude_resolved: !newShowResolved,
      page: 1,
    });
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleNumberItemsChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setCurrentPage(1);
    setItemsPerPage(parseInt(event.target.value));
  };

  const handleResolve = async (
    id: number,
    notes: string,
    resolutionType: ResolutionType,
    backfillJobUrl?: string,
  ) => {
    await noshowAlertsAPI.resolveAlert(
      id,
      notes,
      resolutionType,
      backfillJobUrl,
    );
    setSuccess('Alert resolved successfully');
    loadAlerts();
    loadStats();
  };

  const totalItems = pagination?.total || 0;

  return (
    <Page
      footer={
        alerts.length > 0 && (
          <Paginator
            currentPage={currentPage}
            handleNumberItemsChange={handleNumberItemsChange}
            handlePageChange={handlePageChange}
            itemsLength={totalItems}
            itemsPerPage={itemsPerPage}
          />
        )
      }
      size="full"
      title="No-Show Alerts"
    >
      {resolveModal && (
        <ResolveModal
          alert={resolveModal}
          onClose={() => setResolveModal(null)}
          onResolve={handleResolve}
        />
      )}
      {smsAlertId && (
        <SmsConversation
          alertId={smsAlertId}
          onClose={() => setSmsAlertId(null)}
        />
      )}
      {detailsAlertId && (
        <AlertDetailsModal
          alertId={detailsAlertId}
          onClose={() => setDetailsAlertId(null)}
        />
      )}

      {stats && (
        <Card>
          <Card.Section>
            <Stack gap={32}>
              <TextStack spacing="tight">
                <Body color="light">Total</Body>
                <Body weight="bold">{stats.total}</Body>
              </TextStack>
              <TextStack spacing="tight">
                <Body color="light">No Response</Body>
                <Body weight="bold">{stats.escalated}</Body>
              </TextStack>
              <TextStack spacing="tight">
                <Body color="light">Backfill Needed</Body>
                <Body weight="bold">{stats.backfill_requested}</Body>
              </TextStack>
              <TextStack spacing="tight">
                <Body color="light">Confirmed Present</Body>
                <Body weight="bold">{stats.confirmed_present}</Body>
              </TextStack>
              <TextStack spacing="tight">
                <Body color="light">No-Show</Body>
                <Body weight="bold">{stats.confirmed_noshow}</Body>
              </TextStack>
              <TextStack spacing="tight">
                <Body color="light">Avg Response</Body>
                <Body weight="bold">
                  {stats.avg_response_time_minutes
                    ? `${stats.avg_response_time_minutes} min`
                    : '-'}
                </Body>
              </TextStack>
            </Stack>
          </Card.Section>
        </Card>
      )}

      <Card>
        <Card.Section>
          <Stack vertical gap={16}>
            {error && (
              <Alert
                description={error}
                icon="warning"
                status="danger"
                title="Error"
                onDismiss={() => setError(null)}
              />
            )}
            {success && (
              <Alert
                description={success}
                icon="check"
                status="success"
                title="Success"
                onDismiss={() => setSuccess(null)}
              />
            )}
            <FormColumns layout="tuple">
              <FormElement label="Status">
                <TextSelect
                  value={(filters.status as string) || ''}
                  onChange={handleStatusChange}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </TextSelect>
              </FormElement>
              <FormElement label="Date From">
                <input
                  type="date"
                  value={filters.date_from || ''}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      date_from: e.target.value || undefined,
                    })
                  }
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                  }}
                />
              </FormElement>
              <FormElement label="Date To">
                <input
                  type="date"
                  value={filters.date_to || ''}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      date_to: e.target.value || undefined,
                    })
                  }
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                  }}
                />
              </FormElement>
              <FormElement label="Sort">
                <TextSelect
                  value={filters.sort || '-created_at'}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setFilters({ ...filters, sort: e.target.value })
                  }
                >
                  <option value="-created_at">Newest First</option>
                  <option value="created_at">Oldest First</option>
                  <option value="-escalated_at">Recently Escalated</option>
                </TextSelect>
              </FormElement>
            </FormColumns>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 0',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={showResolved}
                onChange={handleShowResolvedToggle}
              />
              <Body>Include resolved alerts</Body>
            </label>
          </Stack>
        </Card.Section>

        <Table>
          <TableHeader fields={headerFields} />
          <tbody>
            {alerts.map((alert) => (
              <TableRow
                key={alert.id}
                disabled={RESOLVED_STATUSES.includes(alert.status)}
              >
                <TableCell>
                  <TextStack spacing="tight">
                    {alert.order_id ? (
                      <Link
                        to={`/orders/${alert.order_id}/jobs/${alert.job_id}`}
                      >
                        <Body color="theme" weight="medium">
                          Job {alert.job_id}
                        </Body>
                      </Link>
                    ) : (
                      <Body color="theme" weight="medium">
                        Job {alert.job_id}
                      </Body>
                    )}
                  </TextStack>
                </TableCell>
                <TableCell>
                  <TextStack spacing="tight">
                    {alert.worker ? (
                      <Link to={`/workers/${alert.worker.id}`}>
                        <Body color="theme" weight="medium">
                          {alert.worker.name}
                        </Body>
                      </Link>
                    ) : (
                      <Body weight="medium">N/A</Body>
                    )}
                  </TextStack>
                </TableCell>
                <TableCell>
                  <TextStack spacing="tight">
                    <Body weight="medium">
                      {alert.supervisor?.name || 'N/A'}
                    </Body>
                    {alert.supervisor?.phone && (
                      <Caption color="light">{alert.supervisor.phone}</Caption>
                    )}
                  </TextStack>
                </TableCell>
                <TableCell>
                  <TextStack spacing="tight">
                    <Body weight="medium">{alert.customer_name || 'N/A'}</Body>
                  </TextStack>
                </TableCell>
                <TableCell>
                  <PresenceStatus
                    status={alert.status}
                    presenceResponse={alert.presence_response}
                  />
                </TableCell>
                <TableCell>
                  <BackfillStatus
                    status={alert.status}
                    backfillResponse={alert.backfill_response}
                  />
                </TableCell>
                <TableCell>
                  <TextStack spacing="tight">
                    <Body weight="medium">
                      {alert.sms_sent_at
                        ? format(new Date(alert.sms_sent_at), 'MMM dd, h:mm a')
                        : '-'}
                    </Body>
                  </TextStack>
                </TableCell>
                <TableCell>
                  <TextStack spacing="tight">
                    <Body weight="medium">
                      {format(
                        new Date(alert.created_at),
                        'MMM dd, yyyy h:mm a',
                      )}
                    </Body>
                  </TextStack>
                </TableCell>
                <TableCell>
                  <Stack gap={8}>
                    {alert.sms_sent_at && (
                      <Button
                        a11yLabel="View SMS thread"
                        appearance="outline"
                        size="sm"
                        label="SMS"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSmsAlertId(alert.id);
                        }}
                      />
                    )}
                    <Button
                      a11yLabel="View alert details"
                      appearance="outline"
                      size="sm"
                      label="Details"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDetailsAlertId(alert.id);
                      }}
                    />
                    {!['resolved', 'confirmed_present'].includes(
                      alert.status,
                    ) && (
                      <Button
                        a11yLabel="Resolve alert"
                        appearance="outline"
                        size="sm"
                        label="Resolve"
                        onClick={() => setResolveModal(alert)}
                      />
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {loading && (
              <TableRow>
                <TableCell
                  colSpan={headerFields.length}
                  style={{ textAlign: 'center' }}
                >
                  <LoadingState overlayColor="white" />
                </TableCell>
              </TableRow>
            )}
            {!loading && alerts.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={headerFields.length}
                  style={{ textAlign: 'center' }}
                >
                  <Body>No alerts found. Try adjusting your filters.</Body>
                </TableCell>
              </TableRow>
            )}
          </tbody>
        </Table>
      </Card>
    </Page>
  );
};

export default NoshowAlerts;
